import { useState } from 'react';
import WorkflowStepCard from './WorkflowStepCard';
import DocumentsList from './DocumentsList';
import ComputationPanel from './ComputationPanel';
import PaymentsTable from './PaymentsTable';
import NotesList from './NotesList';
import RecurringCycleCard from './RecurringCycleCard';
import ClientCredentialsCard from './ClientCredentialsCard';

const TABS = [
  { id: 'workflow',    label: 'Workflow' },
  { id: 'documents',  label: 'Documents' },
  { id: 'computation', label: 'Computation' },
  { id: 'payments',   label: 'Payments' },
  { id: 'notes',      label: 'Notes' },
];

export default function OrderDetailTabs({ order, onRefresh }) {
  const isRecurring = order.type === 'recurring';
  const showComputation = order.service_id === 'itr-filing';

  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'computation' && !showComputation) return false;
    return true;
  });

  const [active, setActive] = useState(visibleTabs[0]?.id || 'workflow');

  return (
    <div>
      {/* Tab bar */}
      <div className="flex gap-1 bg-[#EEEDFC] rounded-lg p-1 mb-6 w-fit max-w-full overflow-x-auto">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-body whitespace-nowrap transition-all ${
              active === t.id
                ? 'bg-white text-[#6C63FF] shadow-sm font-medium'
                : 'text-[#6B7280] hover:text-[#1A1A2E]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Workflow tab */}
      {active === 'workflow' && (
        isRecurring ? (
          <RecurringCyclesTab order={order} onRefresh={onRefresh} />
        ) : (
          <div className="space-y-3">
            {(order.steps || []).map((step) => (
              <WorkflowStepCard
                key={step.step_number}
                step={step}
                orderId={order.id}
                serviceId={order.service_id}
                order={order}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        )
      )}

      {/* Documents tab */}
      {active === 'documents' && (
        <div className="space-y-5">
          <ClientCredentialsCard orderId={order.id} />
          <DocumentsList
            documents={order.documents || []}
            orderId={order.id}
            steps={order.steps || []}
            onRefresh={onRefresh}
          />
        </div>
      )}

      {/* Computation tab (ITR only) */}
      {active === 'computation' && showComputation && (
        <div className="max-w-xl">
          <ComputationPanel orderId={order.id} order={order} onRefresh={onRefresh} />
        </div>
      )}

      {/* Payments tab */}
      {active === 'payments' && <PaymentsTable payments={order.payments || []} />}

      {/* Notes tab */}
      {active === 'notes' && (
        <NotesList
          notes={order.notes || []}
          orderId={order.id}
          steps={order.steps || []}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

function RecurringCyclesTab({ order, onRefresh }) {
  const [cycles, setCycles] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCycles = async () => {
    if (cycles !== null) return;
    setLoading(true);
    try {
      const API = (await import('../../lib/api')).default;
      const { data } = await API.get(`/admin/orders/${order.id}/cycles`);
      setCycles(data.cycles || []);
    } catch { setCycles([]); }
    setLoading(false);
  };

  if (cycles === null && !loading) loadCycles();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleRefresh = async () => {
    setCycles(null);
    await loadCycles();
    if (onRefresh) await onRefresh();
  };

  if (!cycles || cycles.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-[#9CA3AF] font-body">No cycle records yet. Steps below show the current workflow.</p>
        <div className="space-y-2">
          {(order.steps || []).map((step) => (
            <WorkflowStepCard
              key={step.step_number}
              step={step}
              orderId={order.id}
              serviceId={order.service_id}
              order={order}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {cycles.map((cycle) => (
        <RecurringCycleCard
          key={cycle.month_key}
          cycle={cycle}
          orderId={order.id}
          onRefresh={handleRefresh}
        />
      ))}
    </div>
  );
}
